import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  FileText,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import DeleteConfirmationDialog from '../../components/dialogs/DeleteConfirmationDialog';
import BlogPostEditor from './BlogPostEditor';
import blogApiService from '../../services/blogApiService';
import type { Blog, BlogListResponse } from '../../types/blog';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatRelativeTime, truncateText } from '../../utils/helpers';
import logger from '../../utils/logger';
import toast from 'react-hot-toast';

type StatusFilter = 'all' | 'published' | 'draft' | 'archived';

function getCategoryLabel(category: unknown): string {
  if (typeof category === 'string') {
    return category;
  }

  if (category && typeof category === 'object' && 'name' in category) {
    const value = (category as { name?: unknown }).name;
    if (typeof value === 'string') {
      return value;
    }
  }

  return '';
}

function getFooterStatusText(isUpdating: boolean, activeFooter?: boolean) {
  if (isUpdating) {
    return 'Updating...';
  }

  if (activeFooter) {
    return 'Active';
  }

  return 'Hidden';
}

function getFooterAriaLabel(post: Blog) {
  if (post.activeFooter) {
    return `Disable footer for ${post.title}`;
  }

  return `Enable footer for ${post.title}`;
}

const BlogPostList: React.FC = () => {
  const [posts, setPosts] = useState<Blog[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<Blog | null>(null);
  const [footerToggleId, setFooterToggleId] = useState<number | null>(null);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPost, setDeletingPost] = useState<Blog | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      setIsLoading(true);
      let response: BlogListResponse;

      if (searchTerm.trim()) {
        response = await blogApiService.searchBlogs(searchTerm, currentPage, pageSize);
      } else {
        response = await blogApiService.getBlogs(currentPage, pageSize);
      }

      let filteredContent = response.content;
      if (statusFilter === 'published') {
        filteredContent = filteredContent.filter((p) => p.status?.toUpperCase() === 'PUBLISHED');
      } else if (statusFilter === 'draft') {
        filteredContent = filteredContent.filter((p) => {
          const hasDraftStatus = p.status?.toUpperCase() === 'DRAFT';
          const hasNoStatusAndIsUnpublished = p.status == null && p.published === false;
          return hasDraftStatus || hasNoStatusAndIsUnpublished;
        });
      } else if (statusFilter === 'archived') {
        filteredContent = filteredContent.filter((p) => p.status?.toUpperCase() === 'ARCHIVED');
      }

      setPosts(filteredContent);
      if (statusFilter === 'all') {
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      } else {
        setTotalElements(filteredContent.length);
        setTotalPages(Math.ceil(filteredContent.length / pageSize));
      }
    } catch (error) {
      const message = extractErrorMessage(error);
      toast.error(message);
      logger.error('Failed to fetch blog posts:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setCurrentPage(0);
    },
    []
  );

  const handleCreateNew = () => {
    setEditingPost(null);
    setEditorOpen(true);
  };

  const handleEdit = (post: Blog) => {
    setEditingPost(post);
    setEditorOpen(true);
  };

  const handleDeleteClick = (post: Blog) => {
    setDeletingPost(post);
    setDeleteDialogOpen(true);
  };

  const handleFooterToggle = async (post: Blog) => {
    try {
      setFooterToggleId(post.id);
      const desiredFooterState = !Boolean(post.activeFooter);
      const updatedPost = await blogApiService.toggleBlogFooterActive(post.id, desiredFooterState);
      const nextFooterState =
        typeof updatedPost.activeFooter === 'boolean'
          ? updatedPost.activeFooter
          : desiredFooterState;
      setPosts((prev) =>
        prev.map((item) => (item.id === post.id ? { ...item, activeFooter: nextFooterState } : item))
      );
      toast.success(nextFooterState ? 'Footer active for this post' : 'Footer hidden for this post');
    } catch (error) {
      toast.error(extractErrorMessage(error));
      logger.error('Failed to toggle blog footer from list:', error);
    } finally {
      setFooterToggleId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPost) return;
    try {
      setIsDeleting(true);
      await blogApiService.deleteBlog(deletingPost.id);
      toast.success('Post deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingPost(null);
      fetchPosts();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditorClose = (saved?: boolean) => {
    setEditorOpen(false);
    setEditingPost(null);
    if (saved) fetchPosts();
  };

  const getStatusBadge = (post: Blog) => {
    switch (post.status?.toUpperCase()) {
      case 'PUBLISHED':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
            Published
          </span>
        );
      case 'ARCHIVED':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400">
            Archived
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
            Draft
          </span>
        );
    }
  };

  if (editorOpen) {
    return <BlogPostEditor post={editingPost} onClose={handleEditorClose} />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search posts..."
              className="pl-10 h-9 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
            />
          </div>
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
            {(['all', 'published', 'draft', 'archived'] as StatusFilter[]).map((filter) => (
              <button
                key={filter}
                onClick={() => { setStatusFilter(filter); setCurrentPage(0); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  statusFilter === filter
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <Button
          onClick={handleCreateNew}
          className="bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 text-white dark:text-slate-900 shadow-md"
          size="sm"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          New Post
        </Button>
      </div>

      {/* Posts Grid */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" />
            <p className="text-sm text-slate-500">Loading posts...</p>
          </div>
        </div>
      )}

      {!isLoading && posts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
            <FileText className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">No posts found</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-sm">
            {searchTerm ? 'Try a different search term.' : 'Create your first blog post to get started.'}
          </p>
          {!searchTerm && (
            <Button onClick={handleCreateNew} className="mt-4" size="sm">
              <Plus className="h-4 w-4 mr-1.5" /> Create Post
            </Button>
          )}
        </div>
      )}

      {!isLoading && posts.length > 0 && (
        <>
          {/* Table */}
          <Card className="overflow-hidden border-slate-200 dark:border-slate-800">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50">
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400 w-16" />
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Title</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Author</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Footer</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Categories</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Updated</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-500 dark:text-slate-400 w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {posts.map((post) => (
                    <tr
                      key={post.id}
                      className="group hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="px-4 py-3">
                        {post.coverImage ? (
                          <img
                            src={post.coverImage}
                            alt=""
                            className="h-10 w-14 object-cover rounded-md border border-slate-200 dark:border-slate-700"
                          />
                        ) : (
                          <div className="h-10 w-14 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <FileText className="h-4 w-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100 line-clamp-1">
                            {post.title}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                            /{post.slug}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {post.author?.avatar ? (
                            <img
                              src={post.author.avatar}
                              alt=""
                              className="h-6 w-6 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-6 w-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-400">
                              {post.author?.name?.charAt(0) ?? '?'}
                            </div>
                          )}
                          <span className="text-slate-700 dark:text-slate-300 text-sm">
                            {post.author?.name ?? 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(post)}</td>
                      <td className="px-4 py-3">
                        <div className={`inline-flex items-center gap-2 rounded-full border border-slate-200/80 dark:border-slate-700/70 bg-white dark:bg-slate-900 px-2.5 py-1.5 shadow-sm ${post.status === 'ARCHIVED' ? 'opacity-60' : ''}`}>
                          <button
                            type="button"
                            disabled={footerToggleId === post.id || post.status === 'ARCHIVED'}
                            aria-label={getFooterAriaLabel(post)}
                            onClick={() => handleFooterToggle(post)}
                            className={`relative w-8 h-4 shrink-0 rounded-full transition-colors ${
                              post.status === 'ARCHIVED' 
                                ? 'bg-slate-200 dark:bg-slate-700 cursor-not-allowed'
                                : footerToggleId === post.id
                                  ? 'bg-slate-200 dark:bg-slate-700 cursor-not-allowed opacity-50'
                                  : post.activeFooter 
                                    ? 'bg-emerald-500' 
                                    : 'bg-slate-300 dark:bg-slate-600'
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${
                                post.activeFooter ? 'translate-x-4' : 'translate-x-0'
                              }`}
                            />
                          </button>
                          <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 min-w-[50px]">
                            {post.status === 'ARCHIVED' ? 'Unavailable' : getFooterStatusText(footerToggleId === post.id, post.activeFooter)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {post.categories?.slice(0, 3).map((cat, index) => {
                            const label = getCategoryLabel(cat);
                            if (!label) {
                              return null;
                            }

                            return (
                              <span
                                key={`${label}-${index}`}
                                className="inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"
                              >
                                {truncateText(label, 15)}
                              </span>
                            );
                          })}
                          {(post.categories?.length ?? 0) > 3 && (
                            <span className="text-[10px] text-slate-400">
                              +{post.categories.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {post.updatedAt ? formatRelativeTime(post.updatedAt) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEdit(post)}
                            className="p-1.5 rounded-md text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                            title="Edit"
                            aria-label={`Edit ${post.title}`}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(post)}
                            className="p-1.5 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            title="Delete"
                            aria-label={`Delete ${post.title}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-1">
              <p className="text-xs text-slate-500">
                Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, totalElements)} of{' '}
                {totalElements} posts
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const page = currentPage < 3 ? i : currentPage - 2 + i;
                  if (page >= totalPages) return null;
                  return (
                    <Button
                      key={page}
                      variant={page === currentPage ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={`h-8 w-8 p-0 text-xs ${
                        page === currentPage
                          ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                          : ''
                      }`}
                    >
                      {page + 1}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDeletingPost(null); }}
        onConfirm={handleDeleteConfirm}
        title="Delete Blog Post"
        itemName={deletingPost?.title ?? ''}
        itemType="Blog Post"
        warnings={['All associated sections and blocks will be removed.']}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default BlogPostList;
